import React from 'react';
import { Props } from '@service-victoria/component-library/components/AddressLookup';
import Overseas, {
  ComponentProps as OverseasProps
} from '@service-victoria/component-library/components/AddressLookup/Overseas';
import { addressService } from 'services';
import { url } from 'utils';
import { countryCodes } from 'interfaces/common';
import { StyledWrapper } from './styled';
import { Props as AutoCompleteProps } from '@service-victoria/component-library/components/AutoComplete';
import { countryOptions } from './countries';

type ComponentProps = {
  fieldName: string;
  label: string;
  onSelection?: (
    address: Props.Interfaces.ApiFullAddress,
    selectedValue: AutoCompleteProps.OptionType
  ) => void;
  country?: countryCodes;
  addressType?: OverseasProps['addressType'];
};

const baseURL = url.getSearchApiDomain();
const loadOptions = addressService.getAddressesLookup(baseURL);
const loadAddressFullDetailsHandler = addressService.getAddressDetails(baseURL);

const Address: React.FC<ComponentProps> = ({
  fieldName,
  label,
  onSelection,
  country = 'AUS',
  addressType = 'local'
}) => {
  const singleProps: Props.Single = {
    'aria-label': 'search',
    fieldName,
    label,
    displayPOBox: false,
    country,
    isClearable: true,
    loadOptions,
    loadAddressFullDetailsHandler: async (addressId: string, _, selectedValue) => {
      const result = await loadAddressFullDetailsHandler(addressId, country);

      if (onSelection) {
        // Note - this needs to be wrapped in a setTimeout because the component lib is blowing out the actions of setFieldValue
        setTimeout(() => {
          onSelection(result, selectedValue);
        }, 0);
      }
      return result;
    },
    fullFormProps: {
      fullAddressProps: {
        'data-testid': `${fieldName}-ManualFullAddressInput`,
        errorLabelProps: {
          'data-testid': `${fieldName}-ManualFullAddressInputError`
        }
      },
      suburbProps: {
        maxLength: 40,
        'data-testid': `${fieldName}-ManualSuburbInput`,
        errorLabelProps: {
          'data-testid': `${fieldName}-ManualSuburbInputError`
        }
      },
      stateProps: {
        selectProps: {
          'data-testid': `${fieldName}-ManualStateDropdown`
        },
        errorProps: {
          'data-testid': `${fieldName}-ManualStateDropdownError`
        }
      },
      postCodeProps: {
        'data-testid': `${fieldName}-ManualPostCodeInput`,
        errorLabelProps: {
          'data-testid': `${fieldName}-ManualPostCodeInputError`
        }
      }
    }
  };

  return (
    <StyledWrapper country={country}>
      <Overseas
        localProps={singleProps}
        fieldName={fieldName}
        mode='Basic'
        loadCountriesHandler={() => Promise.resolve(countryOptions)}
        addressType={addressType}
        fullAddressTextAreaProps={{
          'data-testid': `${fieldName}-OverseasFullAddress`,
          maxLength: 120
        }}
        overseasCheckBoxProps={{
          'data-testid': `${fieldName}-OverseasCheckBox`
        }}
        countryDropDownProps={{
          selectProps: {
            'data-testid': `${fieldName}-CountryDropdown`
          }
        }}
      />
    </StyledWrapper>
  );
};

export default Address;
